/**
 * FinishingFlow (DQGMS · P-020 → P-023)
 *
 * Final Finishing operator flow. Four screens in one mini state machine:
 *   finishingScan → koList → koAction → releaseCheck
 *
 * Rendered as the 'finishing' step in App.js. In QGS v2 this reads from
 * the REAL backend — /api/frontend/qgs/reports/knockouts/ filtered by
 * unit_serial + status=OPEN. There is no hardcoded demo fallback; if
 * the unit has no open KOs, the list shows an empty state and the
 * release button unlocks immediately.
 */
import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_BASE_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS_COLORS = {
  OPEN:     { bg: '#fce8e6', text: '#8a2c22' },
  CLOSED:   { bg: '#e6f6ee', text: '#0f5a3a' },
  DEFERRED: { bg: '#fdf0e0', text: '#8a4610' },
};

export default function FinishingFlow({ onExit }) {
  const [step, setStep] = useState('scan'); // 'scan' | 'list' | 'action' | 'release'
  const [unitSerial, setUnitSerial] = useState('');
  const [kos, setKos] = useState([]);
  const [selectedKO, setSelectedKO] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const loadKOsForUnit = async (serial) => {
    setLoading(true);
    setLoadError('');
    try {
      const url = `${API_BASE}/api/frontend/qgs/reports/knockouts/`
        + `?unit_serial=${encodeURIComponent(serial)}`
        + `&status=OPEN&page_size=200`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const rows = (body.data || []).map(k => ({
        ...k,
        // The reports view names the field defect_type / defect_location /
        // creator directly; no remapping needed. Ensure `notes` falls back
        // to initial_comment so the KO Action screen has something to show.
        notes: k.notes || k.initial_comment || '',
      }));
      setKos(rows);
    } catch (e) {
      setLoadError(e.message || 'Failed to load KOs');
      setKos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleScanEntered = (serial) => {
    setUnitSerial(serial);
    loadKOsForUnit(serial);
    setStep('list');
  };

  const handleAddressKO = (ko) => {
    setSelectedKO(ko);
    setStep('action');
  };

  const handleActionDone = async (ko, decision) => {
    // QGS v2 · "Reworked" → request-closure on the real backend (KO stays
    // OPEN with closure_pending=true, waits for LL sign-off in the
    // Approval Queue). "Deferred" is not wired to a real endpoint yet so
    // we still update local state and warn — keep the UI usable while we
    // decide on the deferred workflow.
    if (decision === 'CLOSED') {
      try {
        // Fetch CSRF once, best-effort.
        let csrf = '';
        try {
          const c = await fetch(`${API_BASE}/api/frontend/auth/csrf-token/`, { credentials: 'include' });
          if (c.ok) csrf = (await c.json())?.data?.csrfToken || '';
        } catch {}
        const res = await fetch(`${API_BASE}/api/frontend/qgs/knockouts/${ko.id}/request-closure/`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRFToken': csrf } : {}) },
          body: JSON.stringify({
            rework_type: ko.rework_type || null,
            rework_comment: 'Closed from Final Finishing',
          }),
        });
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try { const b = await res.json(); if (b.error) msg = b.error; } catch {}
          alert(`Failed to submit rework: ${msg}`);
          return;
        }
        // Refresh the KO list so the LL-pending row falls off "OPEN".
        await loadKOsForUnit(unitSerial);
      } catch (e) {
        alert(`Failed to submit rework: ${e.message}`);
        return;
      }
    } else {
      // Deferred / other local-only decisions.
      setKos(prev => prev.map(k => k.ko_id === ko.ko_id ? { ...k, status: decision, days_open: 0 } : k));
    }
    setSelectedKO(null);
    setStep('list');
  };

  const openKOs = kos.filter(k => k.status === 'OPEN');
  const canRelease = openKOs.length === 0;

  return (
    <div style={{ minHeight: '100vh', background: '#f4f7fa' }}>
      {/* Header */}
      <div style={{ background: '#0e2a3f', color: '#fff', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onExit} style={{ border: 'none', background: 'transparent', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
        <div>
          <div style={{ fontSize: 12, letterSpacing: 1.2, color: '#7a8a9b', textTransform: 'uppercase', fontWeight: 700 }}>Final Finishing</div>
          {unitSerial && <div style={{ fontSize: 14, fontWeight: 600 }}>Unit: {unitSerial}</div>}
        </div>
      </div>

      {step === 'scan'    && <FinishingScan onScanned={handleScanEntered} />}
      {step === 'list'    && <KOListScreen kos={kos} loading={loading} loadError={loadError} unitSerial={unitSerial} onAddress={handleAddressKO} onRelease={() => setStep('release')} canRelease={canRelease} />}
      {step === 'action'  && selectedKO && <KOActionScreen ko={selectedKO} onDone={handleActionDone} onCancel={() => { setSelectedKO(null); setStep('list'); }} />}
      {step === 'release' && <ReleaseCheckScreen kos={kos} canRelease={canRelease} onComplete={onExit} onBack={() => setStep('list')} />}
    </div>
  );
}

// ---------- Sub-screens ----------

function FinishingScan({ onScanned }) {
  const [manual, setManual] = useState('');
  return (
    <div style={{ padding: 20, maxWidth: 480, margin: '0 auto' }}>
      <h2 style={{ fontSize: 18, marginBottom: 12 }}>Scan unit QR</h2>
      <div style={{
        background: '#fff', border: '2px dashed #cfd6de', borderRadius: 12,
        padding: 40, textAlign: 'center', marginBottom: 20,
      }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>📷</div>
        <div style={{ color: '#4a596b', fontSize: 14 }}>Position the QR in the frame</div>
        <div style={{ color: '#8592a3', fontSize: 11, marginTop: 4 }}>(Mock — use manual entry below for the demo)</div>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e5eaf0', borderRadius: 8, padding: 14 }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#4a596b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Or enter serial manually</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={manual}
            onChange={e => setManual(e.target.value.toUpperCase())}
            placeholder="CH-26G24262-KKKKXX"
            style={{ flex: 1, padding: 10, border: '1px solid #cfd6de', borderRadius: 6, fontSize: 14, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
          />
          <button
            disabled={manual.length < 4}
            onClick={() => onScanned(manual)}
            style={{
              padding: '10px 18px', border: 'none', borderRadius: 6,
              background: manual.length >= 4 ? '#2b7fd0' : '#8592a3',
              color: '#fff', fontWeight: 700, cursor: manual.length >= 4 ? 'pointer' : 'not-allowed',
            }}
          >Continue</button>
        </div>
      </div>

    </div>
  );
}

function KOListScreen({ kos, loading, loadError, unitSerial, onAddress, onRelease, canRelease }) {
  return (
    <div style={{ padding: 20, maxWidth: 720, margin: '0 auto' }}>
      <h2 style={{ fontSize: 18, marginBottom: 4 }}>Open items for this unit</h2>
      <p style={{ color: '#4a596b', fontSize: 13, marginTop: 0, marginBottom: 14 }}>Address each KO, then proceed to release check.</p>

      {loading && <div style={{ padding: 24, textAlign: 'center', color: '#8592a3' }}>Loading…</div>}
      {!loading && loadError && (
        <div style={{ padding: 20, textAlign: 'center', color: '#8a2c22', background: '#fce8e6', border: '1px solid #f5b7b1', borderRadius: 8 }}>
          Could not load KOs for <code>{unitSerial}</code>: {loadError}
        </div>
      )}
      {!loading && !loadError && kos.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#22a06b', background: '#e6f6ee', borderRadius: 8, fontWeight: 600 }}>
          ✓ No open KOs on {unitSerial}. Ready for release.
        </div>
      )}

      {kos.map(ko => {
        const c = STATUS_COLORS[ko.status] || { bg: '#f1f5f9', text: '#334155' };
        return (
          <div key={ko.ko_id || ko.id} style={{
            background: '#fff', border: '1px solid #e5eaf0', borderRadius: 8,
            padding: 14, marginBottom: 8,
            display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 12, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', color: '#4a596b', marginBottom: 4 }}>
                {ko.ko_id || `#${ko.id}`}
                <span style={{ marginLeft: 8, padding: '2px 7px', borderRadius: 999, background: c.bg, color: c.text, fontSize: 10, fontWeight: 700 }}>
                  {ko.status}
                </span>
                {ko.gate && <span style={{ marginLeft: 6, color: '#8592a3', fontSize: 11 }}>· {ko.gate}</span>}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{ko.defect_type || '—'}</div>
              <div style={{ fontSize: 12, color: '#4a596b' }}>
                {ko.defect_location || '—'}
                {ko.creator ? ` · created by ${ko.creator}` : ''}
              </div>
            </div>
            {ko.status === 'OPEN' && (
              <button onClick={() => onAddress(ko)} style={{ padding: '8px 14px', border: 'none', borderRadius: 6, background: '#d4661f', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                Address
              </button>
            )}
          </div>
        );
      })}

      <button
        onClick={onRelease}
        style={{
          width: '100%', marginTop: 18, padding: '14px 0',
          border: 'none', borderRadius: 8,
          background: canRelease ? '#22a06b' : '#8592a3',
          color: '#fff', fontSize: 16, fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {canRelease ? '✓ Go to release check' : `Release blocked — ${kos.filter(k => k.status === 'OPEN').length} KO still open`}
      </button>
    </div>
  );
}

function KOActionScreen({ ko, onDone, onCancel }) {
  const [decision, setDecision] = useState(null); // 'CLOSED' | 'DEFERRED'
  const [reworkType, setReworkType] = useState('');
  const [deferReason, setDeferReason] = useState('');
  const [approverPin, setApproverPin] = useState('');

  const canSubmit = decision === 'CLOSED' ? reworkType.length > 0 : deferReason.length > 0 && approverPin.length >= 4;

  return (
    <div style={{ padding: 20, maxWidth: 720, margin: '0 auto' }}>
      <button onClick={onCancel} style={{ background: 'transparent', border: 'none', color: '#4a596b', fontSize: 13, cursor: 'pointer', marginBottom: 12 }}>← Back to list</button>

      <div style={{ background: '#fff', border: '1px solid #e5eaf0', borderRadius: 8, padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', color: '#4a596b' }}>{ko.ko_id}</div>
        <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>{ko.defect_type}</div>
        <div style={{ fontSize: 13, color: '#4a596b' }}>{ko.defect_location} · {ko.gate} · created by {ko.creator}</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
        <button
          onClick={() => setDecision('CLOSED')}
          style={{
            padding: 16, border: `2px solid ${decision === 'CLOSED' ? '#22a06b' : '#cfd6de'}`,
            borderRadius: 8, background: decision === 'CLOSED' ? '#22a06b' : '#fff',
            color: decision === 'CLOSED' ? '#fff' : '#22a06b', fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          ✓ Reworked
        </button>
        <button
          onClick={() => setDecision('DEFERRED')}
          style={{
            padding: 16, border: `2px solid ${decision === 'DEFERRED' ? '#d4661f' : '#cfd6de'}`,
            borderRadius: 8, background: decision === 'DEFERRED' ? '#d4661f' : '#fff',
            color: decision === 'DEFERRED' ? '#fff' : '#d4661f', fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          ⏸ Cannot rework — Defer
        </button>
      </div>

      {decision === 'CLOSED' && (
        <div style={{ background: '#e6f6ee', border: '1px solid #22a06b55', borderRadius: 6, padding: 12, marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#0f5a3a', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Rework type</label>
          <select value={reworkType} onChange={e => setReworkType(e.target.value)} style={{ width: '100%', padding: 10, border: '1px solid #cfd6de', borderRadius: 6, fontSize: 13, marginBottom: 8 }}>
            <option value="">Choose rework…</option>
            <option>Re-fit component</option>
            <option>Tighten connection</option>
            <option>Replace part</option>
            <option>Clean surface</option>
          </select>
          <div style={{ fontSize: 12, color: '#0f5a3a' }}>📷 After-photo required — use existing camera flow.</div>
        </div>
      )}

      {decision === 'DEFERRED' && (
        <div style={{ background: '#fdf0e0', border: '1px solid #d4661f55', borderRadius: 6, padding: 12, marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#8a4610', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Deferral reason</label>
          <select value={deferReason} onChange={e => setDeferReason(e.target.value)} style={{ width: '100%', padding: 10, border: '1px solid #cfd6de', borderRadius: 6, fontSize: 13, marginBottom: 10 }}>
            <option value="">Choose reason…</option>
            <option>Parts not available</option>
            <option>Requires supplier action</option>
            <option>To be fixed at customer site</option>
          </select>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#8a4610', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Line Leader PIN</label>
          <input type="password" value={approverPin} onChange={e => setApproverPin(e.target.value)} placeholder="4-6 digits" style={{ width: '100%', padding: 10, border: '1px solid #cfd6de', borderRadius: 6, fontSize: 13, letterSpacing: 4 }} />
        </div>
      )}

      <button
        disabled={!decision || !canSubmit}
        onClick={() => onDone(ko, decision)}
        style={{
          width: '100%', padding: '14px 0', border: 'none', borderRadius: 8,
          background: (decision && canSubmit) ? '#2b7fd0' : '#8592a3',
          color: '#fff', fontSize: 15, fontWeight: 700, cursor: (decision && canSubmit) ? 'pointer' : 'not-allowed',
        }}
      >Confirm decision</button>
    </div>
  );
}

function ReleaseCheckScreen({ kos, canRelease, onComplete, onBack }) {
  return (
    <div style={{ padding: 20, maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
      <div style={{
        margin: '30px auto 20px auto',
        width: 160, height: 160, borderRadius: '50%',
        background: canRelease ? '#e6f6ee' : '#fce8e6',
        display: 'grid', placeItems: 'center',
        fontSize: 72, color: canRelease ? '#22a06b' : '#d94a3a',
        border: `4px solid ${canRelease ? '#22a06b' : '#d94a3a'}`,
      }}>
        {canRelease ? '✓' : '✕'}
      </div>

      <h2 style={{ fontSize: 22, margin: '0 0 8px 0', color: canRelease ? '#22a06b' : '#d94a3a' }}>
        {canRelease ? 'Ready to release' : `Cannot release`}
      </h2>
      <p style={{ color: '#4a596b', fontSize: 14, marginBottom: 20 }}>
        {canRelease
          ? `All KOs resolved. ${kos.filter(k => k.status === 'DEFERRED').length} deferred item(s) will follow the unit downstream.`
          : `${kos.filter(k => k.status === 'OPEN').length} KO still OPEN. Address them before completing.`}
      </p>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button onClick={onBack} style={{ padding: '12px 20px', border: '1px solid #cfd6de', borderRadius: 8, background: '#fff', fontWeight: 600, cursor: 'pointer' }}>
          Back to KO list
        </button>
        <button
          disabled={!canRelease}
          onClick={onComplete}
          style={{
            padding: '12px 20px', border: 'none', borderRadius: 8,
            background: canRelease ? '#22a06b' : '#8592a3',
            color: '#fff', fontWeight: 700, cursor: canRelease ? 'pointer' : 'not-allowed',
          }}
        >
          Complete Finishing
        </button>
      </div>
    </div>
  );
}
